var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
/**
 * useDirectUpload — presign -> direct-to-S3 PUT -> finalize orchestration hook.
 *
 * Wire shapes below match the Frozen contract exactly, snake_case included
 * (.plans/building-attachment-direct-uploads/contracts/direct-uploads-office.md) — this file
 * intentionally disables camelcase/@typescript-eslint/naming-convention rather than camelCasing
 * fields the server actually returns/expects.
 *
 * This hook owns transport-agnostic orchestration only. It takes an injectable `transport`
 * (presign/finalize functions) so the consuming feature stays storage-agnostic — swapping to a
 * different backend (e.g. a later reconciliation with the iOS contract) is a transport swap, not
 * a rewrite of this hook or of the `@ui` FileHandler it pairs with. No S3-specific assumptions
 * leak into `@ui`; this hook is the only place that knows about presigned PUTs.
 *
 * Do not port CustomerFileDrop's UploadManager.tsx header handling — it hardcodes
 * `Content-Type` only (UploadManager.tsx:133). This endpoint signs `acl: 'public-read'`, making
 * `x-amz-acl` a required signed header; every entry in `direct_upload.headers` must be applied
 * verbatim on the PUT (Behavioral Requirement 10 / E2E-46) or every upload fails with
 * `SignatureDoesNotMatch`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
const DEFAULT_CONCURRENCY = 4;
/**
 * Flat 60 minutes, matching `DirectUploads::Storage::UPLOAD_EXPIRES_IN`. The two must stay in step:
 * the client giving up before the URL expires wastes a usable upload, and outliving the URL turns a
 * slow upload into a confusing 403. At 1 GB a 15-minute budget demanded ~9.5 Mbps sustained just to
 * finish, which is not a safe assumption on an office uplink.
 */
const DEFAULT_TIMEOUT_MS = 60 * 60 * 1000;
/**
 * Ceiling on bytes being PUT at once. `concurrency` alone is the wrong control for large files:
 * four 1 GB uploads sharing one uplink each need 4x the bandwidth to finish in the same window, so
 * they fail together. A single file above this budget still uploads alone — see `planUploadWaves`.
 */
const DEFAULT_MAX_BYTES_IN_FLIGHT = 300 * 1024 * 1024;
const DEFAULT_MAX_FILES_PER_REQUEST = 25;
function chunk(items, size) {
    if (size <= 0)
        return [items];
    const chunks = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }
    return chunks;
}
/**
 * Splits a presign chunk into waves bounded by BOTH file count and total bytes.
 *
 * Presigned URLs start expiring the moment they are minted, so a wave is also the presign unit:
 * minting all 25 up front and then uploading 4 at a time meant files late in a batch began against
 * URLs that had been ticking since the batch started. Invisible at a 50 MB cap, fatal at 150 MB+.
 *
 * A file larger than `maxBytes` gets a wave to itself rather than being rejected — otherwise the
 * budget would make large uploads impossible, which is the opposite of the intent.
 */
export function planUploadWaves(items, maxFiles, maxBytes) {
    const waves = [];
    let current = [];
    let bytesInWave = 0;
    items.forEach((item) => {
        var _a, _b;
        const size = (_b = (_a = item.file) === null || _a === void 0 ? void 0 : _a.size) !== null && _b !== void 0 ? _b : 0;
        const wouldExceedCount = current.length >= Math.max(1, maxFiles);
        const wouldExceedBytes = bytesInWave + size > maxBytes;
        if (current.length > 0 && (wouldExceedCount || wouldExceedBytes)) {
            waves.push(current);
            current = [];
            bytesInWave = 0;
        }
        current.push(item);
        bytesInWave += size;
    });
    if (current.length > 0)
        waves.push(current);
    return waves;
}
function runWithConcurrency(items, concurrency, worker) {
    return __awaiter(this, void 0, void 0, function* () {
        let nextIndex = 0;
        const limit = Math.max(1, Math.min(concurrency, items.length || 1));
        const runners = Array.from({ length: Math.min(limit, items.length) }, () => __awaiter(this, void 0, void 0, function* () {
            while (nextIndex < items.length) {
                const current = nextIndex;
                nextIndex += 1;
                // Each runner must finish its assigned upload before taking another item.
                // eslint-disable-next-line no-await-in-loop
                yield worker(items[current], current);
            }
        }));
        yield Promise.all(runners);
    });
}
/** Apply every header the server returned, verbatim, and none it did not (contract Requirement 10 / E2E-46). */
function putFile(file, slot, timeoutMs, onProgress, signal) {
    return new Promise((resolve) => {
        if (signal.aborted) {
            resolve({ ok: false, aborted: true });
            return;
        }
        const xhr = new XMLHttpRequest();
        xhr.timeout = timeoutMs;
        const onAbort = () => {
            xhr.abort();
        };
        signal.addEventListener('abort', onAbort);
        const cleanup = () => {
            signal.removeEventListener('abort', onAbort);
        };
        xhr.upload.onprogress = (ev) => {
            if (ev.lengthComputable) {
                onProgress(Math.round((ev.loaded / ev.total) * 100));
            }
        };
        xhr.onload = () => {
            cleanup();
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve({ ok: true });
            }
            else {
                resolve({ ok: false, errorMessage: `Upload failed: HTTP ${xhr.status}` });
            }
        };
        xhr.onerror = () => {
            cleanup();
            resolve({ ok: false, errorMessage: 'Network error' });
        };
        xhr.ontimeout = () => {
            cleanup();
            resolve({ ok: false, errorMessage: 'Upload timed out' });
        };
        xhr.onabort = () => {
            cleanup();
            resolve({ ok: false, aborted: true });
        };
        xhr.open('PUT', slot.direct_upload.url, true);
        // Apply EVERY header the server sent, and only those — no Content-Type-only shortcut. This
        // is the entire point of the hook: `direct_upload.headers` always includes `x-amz-acl` for
        // this endpoint, and a naive port fails every upload without it.
        Object.entries(slot.direct_upload.headers).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value);
        });
        xhr.send(file);
    });
}
function basenameFor(file) {
    const idx = file.name.lastIndexOf('.');
    return idx > 0 ? file.name.slice(0, idx) : file.name;
}
function toPresignItem(item) {
    return {
        filename: item.file.name,
        content_type: item.file.type || 'application/octet-stream',
        byte_size: item.file.size,
        client_record_id: item.clientRecordId
    };
}
function toFinalizeItem(item, signedId) {
    var _a, _b, _c;
    return {
        signed_id: signedId,
        name: item.name,
        attachment_category_id: (_a = item.attachmentCategoryId) !== null && _a !== void 0 ? _a : null,
        is_private: (_b = item.isPrivate) !== null && _b !== void 0 ? _b : false,
        show_on_customer_portal: (_c = item.showOnCustomerPortal) !== null && _c !== void 0 ? _c : false
    };
}
/**
 * Presign -> concurrent PUT -> finalize orchestration for direct-to-S3 building attachment
 * uploads. Storage-agnostic: everything S3-specific (headers, the PUT itself) lives here, never
 * in `@ui`'s FileHandler. See module doc for the transport seam.
 */
export function useDirectUpload(options) {
    var _a, _b, _c, _d;
    const { transport } = options;
    const concurrency = (_a = options.concurrency) !== null && _a !== void 0 ? _a : DEFAULT_CONCURRENCY;
    const timeoutMs = (_b = options.timeoutMs) !== null && _b !== void 0 ? _b : DEFAULT_TIMEOUT_MS;
    const maxBytesInFlight = (_c = options.maxBytesInFlight) !== null && _c !== void 0 ? _c : DEFAULT_MAX_BYTES_IN_FLIGHT;
    const maxFilesPerRequest = (_d = options.maxFilesPerRequest) !== null && _d !== void 0 ? _d : DEFAULT_MAX_FILES_PER_REQUEST;
    const [items, setItems] = useState([]);
    const [isRunning, setIsRunning] = useState(false);
    // Mirrors `items` synchronously so orchestration code always reads current state instead of a
    // stale closure — React state updates from XHR progress callbacks are async.
    const itemsRef = useRef([]);
    itemsRef.current = items;
    const abortControllerRef = useRef(null);
    useEffect(() => () => {
        var _a;
        (_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.abort();
    }, []);
    const updateItem = useCallback((id, patch) => {
        // Patch the ref too, not just state. `itemsRef` is assigned during render, so every reader
        // between this call and the next render would otherwise see the pre-patch status — including
        // `runBatch`'s `finally` sweep, which would then skip an item that had just become
        // 'finalizing' and leave it stuck on "Finishing up" with no batch running.
        itemsRef.current = itemsRef.current.map((item) => (item.id === id ? Object.assign(Object.assign({}, item), patch) : item));
        setItems((prev) => prev.map((item) => (item.id === id ? Object.assign(Object.assign({}, item), patch) : item)));
    }, []);
    const addFiles = useCallback((files) => {
        if (files.length === 0)
            return;
        const newItems = files.map((file) => ({
            id: uuidv4(),
            file,
            status: 'pending',
            progress: 0,
            clientRecordId: uuidv4(),
            name: basenameFor(file),
            attachmentCategoryId: null,
            isPrivate: false,
            showOnCustomerPortal: false
        }));
        setItems((prev) => [...prev, ...newItems]);
    }, []);
    const updateItemMetadata = useCallback((id, patch) => {
        updateItem(id, patch);
    }, [updateItem]);
    const removeItem = useCallback((id) => {
        setItems((prev) => prev.filter((item) => item.id !== id));
    }, []);
    const reset = useCallback(() => {
        var _a;
        (_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.abort();
        setItems([]);
    }, []);
    /**
     * Presign + PUT a batch of at most `maxFilesPerRequest` items, then finalize whichever of them
     * reached S3. Returns nothing — all state changes go through `updateItem`.
     */
    const processPresignChunk = useCallback((chunkItems, signal) => __awaiter(this, void 0, void 0, function* () {
        if (signal.aborted || chunkItems.length === 0)
            return;
        // Captured locally, not read back out of React state — XHR progress callbacks update state
        // asynchronously, so the finalize payload is built from what presign/PUT actually returned.
        const readyForFinalize = [];
        // Presign per wave, immediately before that wave uploads — never for the whole chunk up
        // front. See `planUploadWaves` for why.
        const waves = planUploadWaves(chunkItems, concurrency, maxBytesInFlight);
        for (const wave of waves) {
            if (signal.aborted)
                break;
            let slots;
            try {
                // eslint-disable-next-line no-await-in-loop
                const response = yield transport.presign({ files: wave.map(toPresignItem) }, signal);
                slots = response.uploads;
            }
            catch (err) {
                const message = err instanceof Error ? err.message : 'Presign failed';
                wave.forEach((item) => updateItem(item.id, { status: 'error', error: message }));
                // A later wave may still succeed — one failed presign must not abandon the rest.
                // eslint-disable-next-line no-continue
                continue;
            }
            wave.forEach((item) => updateItem(item.id, { status: 'uploading', progress: 0 }));
            // eslint-disable-next-line no-await-in-loop
            yield runWithConcurrency(wave, wave.length, (item, index) => __awaiter(this, void 0, void 0, function* () {
                const slot = slots[index];
                if (!slot) {
                    updateItem(item.id, { status: 'error', error: 'No upload slot returned for this file' });
                    return;
                }
                const result = yield putFile(item.file, slot, timeoutMs, (pct) => updateItem(item.id, { progress: pct }), signal);
                if (result.aborted) {
                    updateItem(item.id, { status: 'pending', progress: 0 });
                    return;
                }
                if (!result.ok) {
                    updateItem(item.id, { status: 'error', error: result.errorMessage, progress: 0 });
                    return;
                }
                // 'finalizing', not 'unconfirmed': the bytes are up and finalize has not been tried
                // yet, so this is not a failure. It used to land on 'unconfirmed' here, which the
                // brushaway renders as "Upload failed. Retry to try again." -- every successful upload
                // flashed red for the length of the finalize round trip (IP-16275).
                updateItem(item.id, {
                    status: 'finalizing',
                    progress: 100,
                    signedId: slot.signed_id,
                    s3Key: slot.key
                });
                readyForFinalize.push({ item, signedId: slot.signed_id });
            }));
        }
        if (readyForFinalize.length === 0 || signal.aborted)
            return;
        try {
            const response = yield transport.finalize({
                attachments: readyForFinalize.map(({ item, signedId }) => toFinalizeItem(item, signedId))
            }, signal);
            response.building_attachments.forEach((result, i) => {
                const target = readyForFinalize[i];
                if (target)
                    updateItem(target.item.id, { status: 'done', result });
            });
            // A short response leaves the unmatched tail in 'finalizing' forever; drop it to
            // 'unconfirmed' so those rows stay retry-eligible rather than spinning.
            readyForFinalize.slice(response.building_attachments.length).forEach(({ item }) => {
                updateItem(item.id, { status: 'unconfirmed' });
            });
        }
        catch (_a) {
            // Fix for the CustomerFileDrop confirm-failure bug (UploadManager.tsx:117/141/204-209):
            // the bytes are already in S3 with a captured signedId, so a finalize failure drops to
            // 'unconfirmed' — retry-eligible (re-finalize only), never silently stuck at a 'done'
            // that never happened.
            readyForFinalize.forEach(({ item }) => {
                updateItem(item.id, { status: 'unconfirmed' });
            });
        }
    }), [transport, concurrency, maxBytesInFlight, timeoutMs, updateItem]);
    /** Re-finalize items that already have a captured signedId, without re-PUTting bytes. */
    const processFinalizeOnlyChunk = useCallback((chunkItems, signal) => __awaiter(this, void 0, void 0, function* () {
        if (signal.aborted || chunkItems.length === 0)
            return;
        const withSignedId = chunkItems.filter((item) => Boolean(item.signedId));
        if (withSignedId.length === 0)
            return;
        withSignedId.forEach((item) => updateItem(item.id, { status: 'finalizing' }));
        try {
            const response = yield transport.finalize({
                attachments: withSignedId.map((item) => toFinalizeItem(item, item.signedId))
            }, signal);
            response.building_attachments.forEach((result, i) => {
                const target = withSignedId[i];
                if (target)
                    updateItem(target.id, { status: 'done', result });
            });
            withSignedId.slice(response.building_attachments.length).forEach((item) => {
                updateItem(item.id, { status: 'unconfirmed' });
            });
        }
        catch (_a) {
            // Back to 'unconfirmed' — retry-eligible, same rationale as processPresignChunk above.
            withSignedId.forEach((item) => updateItem(item.id, { status: 'unconfirmed' }));
        }
    }), [transport, updateItem]);
    const runBatch = useCallback((batchItems) => __awaiter(this, void 0, void 0, function* () {
        if (batchItems.length === 0)
            return;
        const controller = new AbortController();
        abortControllerRef.current = controller;
        setIsRunning(true);
        try {
            const needsPresign = batchItems.filter((item) => item.status === 'pending' || item.status === 'error');
            const needsFinalizeOnly = batchItems.filter((item) => item.status === 'unconfirmed');
            needsPresign.forEach((item) => updateItem(item.id, { error: undefined }));
            const presignChunks = chunk(needsPresign, maxFilesPerRequest);
            // eslint-disable-next-line no-restricted-syntax
            for (const presignChunkItems of presignChunks) {
                if (controller.signal.aborted)
                    break;
                // Chunks are processed in sequence so max_files_per_request is respected per network
                // call; concurrency within a chunk is the separate PUT pool above.
                // eslint-disable-next-line no-await-in-loop
                yield processPresignChunk(presignChunkItems, controller.signal);
            }
            const finalizeOnlyChunks = chunk(needsFinalizeOnly, maxFilesPerRequest);
            // eslint-disable-next-line no-restricted-syntax
            for (const finalizeChunkItems of finalizeOnlyChunks) {
                if (controller.signal.aborted)
                    break;
                // eslint-disable-next-line no-await-in-loop
                yield processFinalizeOnlyChunk(finalizeChunkItems, controller.signal);
            }
        }
        finally {
            // 'finalizing' is an in-flight-only state. If the batch ends with rows still in it —
            // cancel aborts before finalize runs, or an early return skips it — they must land on
            // 'unconfirmed' (bytes up, not confirmed) rather than spin forever.
            itemsRef.current.forEach((item) => {
                if (item.status === 'finalizing')
                    updateItem(item.id, { status: 'unconfirmed' });
            });
            setIsRunning(false);
        }
    }), [maxFilesPerRequest, processPresignChunk, processFinalizeOnlyChunk, updateItem]);
    const start = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        const eligible = itemsRef.current.filter((item) => item.status === 'pending' || item.status === 'error' || item.status === 'unconfirmed');
        yield runBatch(eligible);
    }), [runBatch]);
    const retry = useCallback((id) => __awaiter(this, void 0, void 0, function* () {
        const target = itemsRef.current.find((item) => item.id === id);
        if (!target)
            return;
        if (target.status !== 'error' && target.status !== 'unconfirmed')
            return;
        yield runBatch([target]);
    }), [runBatch]);
    const retryFailed = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        const failed = itemsRef.current.filter((item) => item.status === 'error' || item.status === 'unconfirmed');
        yield runBatch(failed);
    }), [runBatch]);
    const cancel = useCallback(() => {
        var _a;
        (_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.abort();
    }, []);
    const totalBytes = items.reduce((sum, item) => sum + item.file.size, 0);
    const weightedBytes = items.reduce((sum, item) => {
        const bytesLanded = item.status === 'done' || item.status === 'finalizing' || item.status === 'unconfirmed';
        const effectiveProgress = bytesLanded ? 100 : item.progress;
        return sum + item.file.size * effectiveProgress;
    }, 0);
    const aggregateProgress = totalBytes === 0 ? 0 : Math.round(weightedBytes / totalBytes);
    return {
        items,
        isRunning,
        aggregateProgress,
        addFiles,
        updateItemMetadata,
        removeItem,
        start,
        retry,
        retryFailed,
        cancel,
        reset
    };
}
export default useDirectUpload;
